import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"

import { fetchOutlookSessionForUser, OutlookPageQuery, OutlookSessionForUserResponse } from "./outlookCreateOrderApi"
import { CreateOrderDialogMode } from "../../../shared/components/createOrder/createOrderTypes"
import { useCreateOrderUser } from "../../../shared/components/createOrder/hooks/useCreateOrderUser"
import { ensureOfficeInitializeWithLog, loadOfficeJs } from "./components/loadOfficeJs"
import { OutlookCreateOrderContent } from "./components/OutlookCreateOrderContent"
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner"
import { useLocaleSelect } from "../../localization/localizationHooks"
import { useApiQuery } from "../../../core/api/useApiQuery"

const transformData = (data: OutlookSessionForUserResponse) => data?.session?.values

export function OutlookCreateOrder() {
    const [searchParams] = useSearchParams()
    const { supportedLocales, selectedLocale, selectLocale } = useLocaleSelect()

    const flowParam = searchParams.get("flow") ?? null
    const modeParam = searchParams.get("mode") ?? null
    const orderId = searchParams.get("orderId")
    const parentOrigin = searchParams.get("parentOrigin") || "*"
    const sessionUid = searchParams.get("sessionUid") ?? null
    const localeParam = searchParams.get("locale") ?? null
    // Per-order opt-in recorded by the add-in on the previous save; honoured only in edit mode.
    const initialShareOutlookAttendees = searchParams.get("shareOutlookAttendees") === "1"
    const isValidLocaleParam = !!localeParam && supportedLocales.includes(localeParam)
    const isLocaleSyncing = isValidLocaleParam && localeParam !== selectedLocale

    const mode = orderId ? CreateOrderDialogMode.EDIT : CreateOrderDialogMode.CREATE

    const { onBehalf, setOnBehalf } = useCreateOrderUser({
        mode,
    })

    const { data: session, isFetching } = useApiQuery(fetchOutlookSessionForUser, {
        queryName: OutlookPageQuery.OUTLOOK_SESSION_FOR_USER,
        dependencies: {
            userId: onBehalf.userId,
            sessionUid: sessionUid!,
        },
        enabled: !!sessionUid,
        select: transformData,
    })

    useEffect(() => {
        if (isLocaleSyncing) {
            selectLocale(localeParam)
        }
    }, [isLocaleSyncing, selectLocale])

    useEffect(() => {
        let disposed = false

        ensureOfficeInitializeWithLog(() => {
            try {
                const parentOrigin = new URLSearchParams(location.search).get("parentOrigin") || "*"
                const payload = { type: "ping:init", ts: new Date().toISOString() }
                Office.context.ui.messageParent(JSON.stringify(payload), { targetOrigin: parentOrigin })
            } catch (e) {
                console.error("messageParent error", e)
            }
        })
        ;(async () => {
            try {
                await loadOfficeJs()

                const readyOrTimeout = await Promise.race([
                    Office.onReady(),
                    new Promise((_, reject) => setTimeout(() => reject(new Error("onReady timeout (10s)")), 10000)),
                ])

                if (disposed) return

                console.log("Office.onReady resolved", readyOrTimeout)
            } catch (e) {
                console.error("office.js init error", e)
                console.log("testOutlookInit check:", {
                    href: location.href,
                    referrer: document.referrer,
                    inIFrame: window.self !== window.top,
                    hasOfficeObj: !!(window as any).Office,
                    hasOnReady: !!(window as any).Office?.onReady,
                })
            }
        })()

        return () => {
            disposed = true
        }
    }, [])

    if (isFetching) {
        return <LoadingSpinner />
    }

    return (
        <OutlookCreateOrderContent
            orderId={orderId ? +orderId : undefined}
            mode={mode}
            flowParam={flowParam}
            modeParam={modeParam}
            onBehalf={onBehalf}
            setOnBehalf={setOnBehalf}
            parentOrigin={parentOrigin}
            session={session}
            initialShareOutlookAttendees={initialShareOutlookAttendees}
        />
    )
}
